import React from 'react';
import { Zap, Users, Layers, DollarSign, CreditCard, MessageSquare, Video, Image, ShoppingBag, BarChart3, Shield, Heart, TrendingUp, Lock, Globe, Star, Sparkles, Play, Camera, Music, Mic, BookOpen, Award, Gift, Bell, Calendar, Send, Eye, ThumbsUp, Headphones, Palette, PenTool, Coffee, Briefcase, Map, Cpu, Wifi, Cloud, Database, FileText, Settings, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const iconMap = {
  Users, Layers, DollarSign, CreditCard, MessageSquare, Video, Image, ShoppingBag, BarChart3, Shield,
  Heart, TrendingUp, Lock, Globe, Star, Sparkles, Zap, Play, Camera, Music, Mic, BookOpen, Award,
  Gift, Bell, Calendar, Send, Eye, ThumbsUp, Headphones, Palette, PenTool, Coffee, Briefcase,
  Map, Cpu, Wifi, Cloud, Database, FileText, Settings, CheckCircle, AlertCircle, Info,
};

const resolveIcon = (name) => {
  if (!name) return Users;
  if (typeof name === 'function') return name;
  return iconMap[name] || Users;
};

const badge = 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08]';

const STEP_META = [
  { icon: 'Users' },
  { icon: 'Eye' },
  { icon: 'Heart' },
];

const EN_DEFAULT_STEP_NUMBERS = ['01', '02', '03'];

const EN_DEFAULTS = {
  heading: 'Getting started takes minutes',
  subheading: 'Three simple steps to unlock a whole new world of content.',
  steps: [
    { number: '01', title: 'Create your account', description: 'Sign up free in seconds — no commitment, no hidden fees.', icon: 'Users' },
    { number: '02', title: 'Find creators you love', description: 'Browse and discover creators across every category and interest.', icon: 'Eye' },
    { number: '03', title: 'Subscribe & enjoy', description: 'Unlock exclusive content, chat directly, and support your favorites.', icon: 'Heart' },
  ],
};

const HowItWorksBlock = () => {
  const { t } = useTranslation('auth');
  const translatedSteps = t('landing.howItWorks.steps', { returnObjects: true, defaultValue: [] });
  const stepNumbers = t('landing.howItWorks.stepNumbers', { returnObjects: true, defaultValue: EN_DEFAULT_STEP_NUMBERS });
  const d = {
    badge: t('landing.howItWorks.badge', 'HOW IT WORKS'),
    heading: t('landing.howItWorks.heading', EN_DEFAULTS.heading),
    subheading: t('landing.howItWorks.subheading', EN_DEFAULTS.subheading),
    steps: STEP_META.map((meta, i) => ({ ...meta, number: stepNumbers[i], title: translatedSteps[i]?.title, description: translatedSteps[i]?.description })),
  };

  return (
    <section className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 sm:mb-20">
          <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 ${badge} rounded-full mb-6`}>
            <Zap className="w-3 h-3" style={{ color: 'var(--color-primary, #10b981)' }} />
            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.15em]">{d.badge}</span>
          </div>
          <h2 className="cl-serif text-3xl sm:text-4xl lg:text-[3.25rem] font-black tracking-[-0.02em] leading-[1.1] mb-4">{d.heading}</h2>
          <p className="text-base text-gray-500 font-light">{d.subheading}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8 relative">
          <div className="hidden md:block absolute top-[60px] left-[16.67%] right-[16.67%] h-[1px] bg-gradient-to-r from-transparent via-gray-300 dark:via-white/[0.08] to-transparent" />
          {d.steps.map((step, i) => {
            const Icon = resolveIcon(step.icon);
            return (
              <div key={i} className="text-center relative">
                <div className="w-[120px] h-[120px] mx-auto mb-8 rounded-full relative flex items-center justify-center bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06]">
                  <div className="absolute inset-0 rounded-full" style={{ background: `linear-gradient(135deg, var(--color-primary, #10b981), transparent)`, opacity: 0.08 }} />
                  <Icon className="w-7 h-7 relative z-10" style={{ color: 'var(--color-primary, #10b981)' }} />
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gray-900 dark:bg-white text-white dark:text-black text-xs font-bold flex items-center justify-center">{step.number}</div>
                </div>
                <h3 className="cl-serif text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed font-light max-w-xs mx-auto">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksBlock;
